import api from '@/lib/api';
import type { ConfirmSaleRequest, RetailProduct, RetailPosSettings, SaleReceipt } from '@pms/shared';

// ─── Query types ──────────────────────────────────────────────────────────────

export interface PosProductsQuery {
  propertyId: string;
  search?: string | undefined;
  departmentId?: string | undefined;
}

// ─── API functions ────────────────────────────────────────────────────────────

/** In-stock, active products for the sell terminal grid (property-scoped). */
export async function getPosProducts(query: PosProductsQuery): Promise<RetailProduct[]> {
  const { data } = await api.get<RetailProduct[]>('/retail/pos/products', { params: query });
  return data;
}

/** Per-property POS settings — the terminal reads `taxRatePercent` for the
 *  optimistic cart total (server remains authoritative at tender). */
export async function getPosSettings(propertyId: string): Promise<RetailPosSettings> {
  const { data } = await api.get<RetailPosSettings>('/retail/pos-settings', {
    params: { propertyId },
  });
  return data;
}

/**
 * Confirm a sale. The `Idempotency-Key` header makes the tender attempt safely
 * retryable: the server replays the same receipt for a repeated key rather than
 * double-selling / double-decrementing stock.
 */
export async function confirmSale(
  payload: ConfirmSaleRequest,
  idempotencyKey: string,
): Promise<SaleReceipt> {
  const { data } = await api.post<SaleReceipt>('/retail/pos/sales', payload, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return data;
}

/** Canonical receipt fetch — backs reprint (pure render, never re-posts). */
export async function getReceipt(id: string): Promise<SaleReceipt> {
  const { data } = await api.get<SaleReceipt>(`/retail/pos/sales/${id}`);
  return data;
}
